import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { citySpecialties } from '@/data/citySpecialties';

interface CitySpecialtyLink {
  cityName: string;
  citySlug: string;
  cityStateProvince: string | null;
  cityCountry: string;
  specialtyName: string;
  specialtySlug: string;
  url: string;
}

interface UseCitySpecialtiesOptions {
  page?: number;
  pageSize?: number;
}

/**
 * Hook to fetch paginated city-specialty combinations from database
 * For sitemap pagination: 150 links per page
 */
export const useCitySpecialtiesFromDatabase = ({
  page = 1,
  pageSize = 150
}: UseCitySpecialtiesOptions = {}) => {
  return useQuery({
    queryKey: ['city-specialties-paginated', page, pageSize],
    queryFn: async () => {
      // Calculate how many cities we need for this page
      // If we have 26 specialties, and want 150 links per page:
      // We need ceil(150/26) = 6 cities per page
      const citiesPerPage = Math.ceil(pageSize / citySpecialties.length);
      const offset = (page - 1) * citiesPerPage;

      // Get total count
      const { count: totalCities } = await supabase
        .from('cities')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Fetch the cities for this page
      const { data: cities, error } = await supabase
        .from('cities')
        .select('name, slug, state_province, country')
        .eq('is_active', true)
        .order('name')
        .range(offset, offset + citiesPerPage - 1);

      if (error) throw error;
      if (!cities) return { links: [], totalCount: 0, totalPages: 0 };

      // Generate all city-specialty combinations
      const links: CitySpecialtyLink[] = [];
      cities.forEach(city => {
        citySpecialties.forEach(specialty => {
          const fullLocation = city.state_province 
            ? `${city.name}, ${city.state_province}` 
            : `${city.name}, ${city.country}`;
          
          links.push({
            cityName: city.name,
            citySlug: city.slug,
            cityStateProvince: city.state_province,
            cityCountry: city.country,
            specialtyName: specialty.names.en,
            specialtySlug: specialty.slugs.en,
            url: `/city/${city.slug}/${specialty.slugs.en}`
          });
        });
      });

      // Calculate totals
      const totalLinks = (totalCities || 0) * citySpecialties.length;
      const totalPages = Math.ceil(totalLinks / pageSize);

      return {
        links: links.slice(0, pageSize), // Return exactly pageSize links
        totalCount: totalLinks,
        totalPages,
        currentPage: page
      };
    },
    staleTime: 1000 * 60 * 10, // 10 minutes cache
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Get total count of city-specialty pages
 */
export const useCitySpecialtiesCount = () => {
  return useQuery({
    queryKey: ['city-specialties-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('cities')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      return {
        totalCities: count || 0,
        totalSpecialties: citySpecialties.length,
        totalPages: (count || 0) * citySpecialties.length
      };
    },
    staleTime: 1000 * 60 * 30, // 30 minutes cache
  });
};
